// Freebie identification + KPI calculation helpers.
// The whitelist is admin-managed via /api/settings/freebie-skus and stored in MongoDB.
// App.js hydrates the in-memory cache on startup; the default below is only the
// initial fallback used before the first fetch completes (or if the API is unreachable).

import axios from "axios";

export const DEFAULT_FREEBIE_SKUS = [
  "WCCE-SC-P6-UF", "WCCE-SC-P6-TT", "WCCE-SC-P6-FF", "WCCE-SC-P6-KK",
  "WCCE-SC-P30-UF", "WCCE-SC-P30-TT", "WCCE-SC-P30-FF", "WCCE-SC-P30-KK",
  "WCCE-WF-P6-OG", "WCCE-WF-P6-CH", "WCCE-WF-P10-OG", "WCCE-WF-P10-CH",
  "SHAKER_OFFLINE", "ORB-BLK-1", "ORB-TEAL-1",
];

// Mutable in-memory cache + Set for O(1) lookups.
let _freebieSkus = [...DEFAULT_FREEBIE_SKUS];
let _freebieSet = new Set(_freebieSkus);
const _subscribers = new Set();

export const getFreebieSkus = () => _freebieSkus;

export const setFreebieSkus = (skus) => {
  _freebieSkus = Array.isArray(skus) ? [...skus] : [];
  _freebieSet = new Set(_freebieSkus);
  _subscribers.forEach((fn) => {
    try { fn(_freebieSkus); } catch { /* ignore subscriber errors */ }
  });
};

// React components can subscribe to cache updates (returns unsubscribe fn)
export const subscribeFreebieSkus = (fn) => {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
};

// Fetch current whitelist from backend and update cache. Safe to call from App startup.
export const loadFreebieSkus = async (apiBase) => {
  try {
    const res = await axios.get(`${apiBase}/settings/freebie-skus`, { withCredentials: true });
    const skus = res?.data?.skus;
    if (Array.isArray(skus)) setFreebieSkus(skus);
    return skus;
  } catch {
    // Leave defaults in place on failure.
    return null;
  }
};

export const isFreebieSkuId = (skuId) => {
  if (!skuId) return false;
  return _freebieSet.has(String(skuId).trim());
};

// API-compatibility shims (previous signature took a productMap — no longer needed).
export const buildItemCodeMap = () => ({});
export const isFreebieLine = (item /* , _map */) => isFreebieSkuId(item?.sku_id);

// Sum MRP × Qty for an array of line items (filterFn optional)
const sumMrpQty = (items = [], filterFn = null) => {
  let total = 0;
  for (const it of items) {
    if (filterFn && !filterFn(it)) continue;
    total += (Number(it?.mrp) || 0) * (Number(it?.quantity) || 0);
  }
  return total;
};

// Total Freebie Value = Σ(MRP × Qty) for all line items (sale + freebies) whose sku_id ∈ FREEBIE_SKUS
// Avg. Freebie %      = Total Freebie Value ÷ Σ(MRP × Qty) for ALL line items × 100
export const calcFreebieKpis = (quotations = []) => {
  let freebieTotal = 0;
  let grandTotal = 0;
  for (const q of quotations) {
    const allLines = [...(q.items || []), ...(q.freebies || [])];
    for (const ln of allLines) {
      const val = (Number(ln?.mrp) || 0) * (Number(ln?.quantity) || 0);
      grandTotal += val;
      if (isFreebieSkuId(ln?.sku_id)) freebieTotal += val;
    }
  }
  return {
    totalFreebieValue: freebieTotal,
    avgFreebiePct: grandTotal > 0 ? (freebieTotal / grandTotal) * 100 : 0,
  };
};

export { sumMrpQty };
